/**
 * IndexedDB storage backend for browser
 */
import { openDB, DBSchema, IDBPDatabase } from 'idb';
import type { DAGObject, DAGStoreBackend } from '@amorphous/core';

interface AmorphousDB extends DBSchema {
  objects: {
    key: string;
    value: DAGObject;
    indexes: {
      'by-type': string;
      'by-creator': string;
      'by-timestamp': number;
    };
  };
  identity: {
    key: string;
    value: string;
  };
  settings: {
    key: string;
    value: unknown;
  };
}

const DB_NAME = 'amorphous';
const DB_VERSION = 1;

let dbPromise: Promise<IDBPDatabase<AmorphousDB>> | null = null;
let dbFailed = false;

// Timeout wrapper for IndexedDB operations
function withTimeout<T>(promise: Promise<T>, ms: number, operation: string): Promise<T> {
  return Promise.race([
    promise,
    new Promise<T>((_, reject) =>
      setTimeout(() => reject(new Error(`IndexedDB ${operation} timed out after ${ms}ms`)), ms)
    ),
  ]);
}

export async function getDB(): Promise<IDBPDatabase<AmorphousDB>> {
  if (dbFailed) {
    throw new Error('IndexedDB previously failed');
  }
  if (!dbPromise) {
    dbPromise = withTimeout(
      openDB<AmorphousDB>(DB_NAME, DB_VERSION, {
        upgrade(db) {
          // Objects store
          const objectStore = db.createObjectStore('objects', { keyPath: 'hash' });
          objectStore.createIndex('by-type', 'type');
          objectStore.createIndex('by-creator', 'creator');
          objectStore.createIndex('by-timestamp', 'timestamp');

          // Identity store
          db.createObjectStore('identity');

          // Settings store
          db.createObjectStore('settings');
        },
      }),
      5000,
      'open'
    ).catch(err => {
      dbFailed = true;
      dbPromise = null;
      throw err;
    });
  }
  return dbPromise;
}

/**
 * IndexedDB implementation of DAGStoreBackend
 */
export class IndexedDBBackend implements DAGStoreBackend {
  async put(obj: DAGObject): Promise<void> {
    const db = await getDB();
    await db.put('objects', obj);
  }

  async get(hash: string): Promise<DAGObject | null> {
    const db = await getDB();
    return (await db.get('objects', hash)) ?? null;
  }

  async has(hash: string): Promise<boolean> {
    const db = await getDB();
    return (await db.get('objects', hash)) !== undefined;
  }

  async getAll(): Promise<DAGObject[]> {
    const db = await getDB();
    return db.getAll('objects');
  }

  /**
   * Get all hashes without loading full objects (more efficient for sync)
   */
  async getAllHashes(): Promise<string[]> {
    const db = await getDB();
    return db.getAllKeys('objects');
  }

  async delete(hash: string): Promise<void> {
    const db = await getDB();
    await db.delete('objects', hash);
  }

  async clear(): Promise<void> {
    const db = await getDB();
    await db.clear('objects');
  }
}

/**
 * Save identity to IndexedDB
 */
export async function saveIdentity(identityJson: string): Promise<void> {
  const db = await getDB();
  await db.put('identity', identityJson, 'current');
}

/**
 * Load identity from IndexedDB
 */
export async function loadIdentity(): Promise<string | null> {
  const db = await getDB();
  return (await db.get('identity', 'current')) ?? null;
}

/**
 * Save a setting
 */
export async function saveSetting(key: string, value: unknown): Promise<void> {
  const db = await getDB();
  await db.put('settings', value, key);
}

/**
 * Load a setting
 */
export async function loadSetting<T>(key: string): Promise<T | null> {
  const db = await getDB();
  return (await db.get('settings', key)) as T ?? null;
}
